import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Stack,
  Group,
  Button,
  Text,
  Textarea,
  Badge,
  Progress,
  SegmentedControl,
  Breadcrumbs,
  Anchor,
  Loader,
  Alert,
  Title,
  Collapse,
  Box,
  SimpleGrid,
  ActionIcon,
  Image,
  FileButton,
  ThemeIcon,
  TextInput,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconArrowRight,
  IconAlertCircle,
  IconCheck,
  IconChevronDown,
  IconChevronUp,
  IconMapPin,
  IconCamera,
  IconX,
  IconPhoto,
  IconQrcode,
  IconList,
  IconSearch,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { modals } from '@mantine/modals';
import { QRScanner } from '../assets/components/QRScanner';

export function AuditReviewPage() {
  const { auditId } = useParams<{ auditId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [audit, setAudit] = useState<any>(null);
  const [assets, setAssets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialIndexSet, setInitialIndexSet] = useState(false);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [status, setStatus] = useState<'pending' | 'found' | 'missing' | 'damaged'>('pending');
  const [notes, setNotes] = useState('');
  const [showMoreDetails, setShowMoreDetails] = useState(false);
  const [auditPhotos, setAuditPhotos] = useState<File[]>([]);
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState<string[]>([]);

  // Scan mode state
  const [reviewMode, setReviewMode] = useState<'sequential' | 'scan'>('sequential');
  const [scanSearchQuery, setScanSearchQuery] = useState('');
  const [scannedAsset, setScannedAsset] = useState<any>(null);
  const [scanNotFound, setScanNotFound] = useState<string | null>(null);

  const current = assets[currentIndex];

  useEffect(() => {
    loadData();
  }, [auditId]);

  // Set initial index from query param after assets are loaded
  useEffect(() => {
    if (assets.length > 0 && !initialIndexSet) {
      const startAt = searchParams.get('startAt');
      if (startAt) {
        const index = parseInt(startAt, 10);
        if (!isNaN(index) && index >= 0 && index < assets.length) {
          setCurrentIndex(index);
        }
      }
      setInitialIndexSet(true);
    }
  }, [assets, searchParams, initialIndexSet]);

  useEffect(() => {
    if (current) {
      setStatus(current.status);
      setNotes(current.notes || '');
      // Reset photos when changing assets
      setAuditPhotos([]);
      setPhotoPreviewUrls([]);
      setShowMoreDetails(false);
    }
  }, [currentIndex, current]);

  async function loadData() {
    if (!auditId) return;

    setLoading(true);
    setError(null);

    try {
      const authKey = Object.keys(localStorage).find(key =>
        key.startsWith('sb-') && key.endsWith('-auth-token')
      );

      if (!authKey) {
        throw new Error('Not authenticated');
      }

      const authData = JSON.parse(localStorage.getItem(authKey) || '{}');
      const token = authData?.access_token;

      if (!token) {
        throw new Error('Not authenticated');
      }

      const baseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      // Fetch audit
      const auditRes = await fetch(
        `${baseUrl}/rest/v1/asset_audit_events?id=eq.${auditId}`,
        {
          headers: {
            'apikey': anonKey,
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      const auditData = await auditRes.json();

      if (!auditData || auditData.length === 0) {
        throw new Error('Audit not found');
      }

      // Fetch assets with site, location, category, and department
      const assetsRes = await fetch(
        `${baseUrl}/rest/v1/asset_audits?audit_event_id=eq.${auditId}&select=*,asset:assets(*,site:sites(id,name),location:locations(id,name),category:categories(id,name),department:departments(id,name))&order=created_at.asc`,
        {
          headers: {
            'apikey': anonKey,
            'Authorization': `Bearer ${token}`,
          },
        }
      );
      const assetsData = await assetsRes.json();

      setAudit(auditData[0]);
      setAssets(assetsData || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load audit');
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  }

  // Handle QR scan result
  async function handleScanResult(scannedData: string) {
    setScanNotFound(null);

    // First, try to find asset by tag_id, guid, or serial_number in the audit list
    let found = assets.find(item => {
      const asset = item.asset;
      if (!asset) return false;

      // Check tag_id (exact match)
      if (asset.tag_id && asset.tag_id.toLowerCase() === scannedData.toLowerCase()) return true;

      // Check guid (QR codes often contain the full URL or guid)
      if (asset.guid && scannedData.includes(asset.guid)) return true;

      // Check serial_number
      if (asset.serial_number && asset.serial_number.toLowerCase() === scannedData.toLowerCase()) return true;

      // Check if scanned data contains the tag_id
      if (asset.tag_id && scannedData.toLowerCase().includes(asset.tag_id.toLowerCase())) return true;

      return false;
    });

    // If not found and scanned data looks like a URL with a token, look up the token
    if (!found && scannedData.includes('/a/')) {
      try {
        // Extract token from URL (e.g., https://assets.trustpuma.com/a/Mj4IAKWx)
        const tokenMatch = scannedData.match(/\/a\/([A-Za-z0-9_-]+)/);
        if (tokenMatch) {
          const token = tokenMatch[1];

          const authKey = Object.keys(localStorage).find(key =>
            key.startsWith('sb-') && key.endsWith('-auth-token')
          );
          const authData = JSON.parse(localStorage.getItem(authKey!) || '{}');
          const accessToken = authData?.access_token;

          const baseUrl = import.meta.env.VITE_SUPABASE_URL;
          const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

          // Look up the asset_id from the token
          const tokenRes = await fetch(
            `${baseUrl}/rest/v1/asset_tokens?token=eq.${token}&select=asset_id`,
            {
              headers: {
                'apikey': anonKey,
                'Authorization': `Bearer ${accessToken}`,
              },
            }
          );
          const tokenData = await tokenRes.json();

          if (tokenData && tokenData.length > 0) {
            const assetId = tokenData[0].asset_id;

            // Find this asset in our audit list
            found = assets.find(item => item.asset?.id === assetId);
          }
        }
      } catch (err) {
        console.error('Error looking up token:', err);
      }
    }

    if (found) {
      setScannedAsset(found);
      setStatus(found.status || 'pending');
      setNotes(found.notes || '');
      setScanSearchQuery('');
    } else {
      setScanNotFound(scannedData);
      setScannedAsset(null);
    }
  }

  // Handle manual search in scan mode
  function handleManualSearch() {
    if (!scanSearchQuery.trim()) return;
    handleScanResult(scanSearchQuery.trim());
  }

  // Quick save in scan mode - mark as found and return to scanner
  async function quickSave(quickStatus: 'found' | 'missing' | 'damaged') {
    if (!scannedAsset) return;

    try {
      const authKey = Object.keys(localStorage).find(key =>
        key.startsWith('sb-') && key.endsWith('-auth-token')
      );
      const authData = JSON.parse(localStorage.getItem(authKey!) || '{}');
      const token = authData?.access_token;

      const baseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      await fetch(`${baseUrl}/rest/v1/asset_audits?id=eq.${scannedAsset.id}`, {
        method: 'PATCH',
        headers: {
          'apikey': anonKey,
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=minimal',
        },
        body: JSON.stringify({
          status: quickStatus,
          notes: notes || null,
          completed_at: new Date().toISOString(),
        }),
      });

      // Update local state
      setAssets(prev =>
        prev.map(a =>
          a.id === scannedAsset.id
            ? { ...a, status: quickStatus, notes, completed_at: new Date().toISOString() }
            : a
        )
      );

      notifications.show({
        title: 'Saved',
        message: `${scannedAsset.asset?.tag_id} marked as ${quickStatus}`,
        color: 'green',
      });

      // Return to scanner
      setScannedAsset(null);
      setNotes('');
      setScanSearchQuery('');
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: 'Failed to save',
        color: 'red',
      });
    }
  }

  async function saveAndNext() {
    if (!current) return;

    try {
      const authKey = Object.keys(localStorage).find(key =>
        key.startsWith('sb-') && key.endsWith('-auth-token')
      );
      const authData = JSON.parse(localStorage.getItem(authKey!) || '{}');
      const token = authData?.access_token;

      const baseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      await fetch(`${baseUrl}/rest/v1/asset_audits?id=eq.${current.id}`, {
        method: 'PATCH',
        headers: {
          'apikey': anonKey,
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=minimal',
        },
        body: JSON.stringify({
          status,
          notes,
          completed_at: new Date().toISOString(),
        }),
      });

      // Update local state
      setAssets(prev =>
        prev.map(a =>
          a.id === current.id
            ? { ...a, status, notes, completed_at: new Date().toISOString() }
            : a
        )
      );

      // Move to next or finish
      if (currentIndex < assets.length - 1) {
        setCurrentIndex(prev => prev + 1);
      } else {
        // All items reviewed - offer to complete the audit
        modals.openConfirmModal({
          title: 'Complete Audit?',
          children: (
            <Text size="sm">
              All assets have been reviewed. Would you like to mark this audit as completed?
            </Text>
          ),
          labels: { confirm: 'Complete Audit', cancel: 'Keep In Progress' },
          confirmProps: { color: 'green', leftSection: <IconCheck size={16} /> },
          onConfirm: async () => {
            try {
              // Update audit_event status to completed
              await fetch(`${baseUrl}/rest/v1/asset_audit_events?id=eq.${auditId}`, {
                method: 'PATCH',
                headers: {
                  'apikey': anonKey,
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                  'Prefer': 'return=minimal',
                },
                body: JSON.stringify({
                  status: 'completed',
                  updated_at: new Date().toISOString(),
                }),
              });
              notifications.show({
                title: 'Audit Completed',
                message: 'The audit has been marked as completed',
                color: 'green',
              });
            } catch {
              notifications.show({
                title: 'Error',
                message: 'Failed to complete audit',
                color: 'red',
              });
            }
            navigate(`/audits/${auditId}`);
          },
          onCancel: () => {
            notifications.show({
              title: 'Review Complete',
              message: 'All assets reviewed. Audit remains in progress.',
              color: 'blue',
            });
            navigate(`/audits/${auditId}`);
          },
        });
      }
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: 'Failed to save',
        color: 'red',
      });
    }
  }

  if (loading) {
    return (
      <Container fluid py="xl">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text>Loading review...</Text>
        </Stack>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid py="xl">
        <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red">
          {error}
        </Alert>
        <Button mt="md" onClick={() => navigate('/audits')}>
          Back to Audits
        </Button>
      </Container>
    );
  }

  if (!audit || assets.length === 0) {
    return (
      <Container fluid py="xl">
        <Text>No assets to review</Text>
        <Button mt="md" onClick={() => navigate(`/audits/${auditId}`)}>
          Back to Audit
        </Button>
      </Container>
    );
  }

  if (!current) {
    return (
      <Container fluid py="xl">
        <Loader />
      </Container>
    );
  }

  const completed = assets.filter(a => a.status !== 'pending').length;
  const progress = (completed / assets.length) * 100;

  // Photo handling
  const handlePhotoAdd = (files: File[]) => {
    const newPhotos = [...auditPhotos, ...files].slice(0, 4); // Max 4 photos
    setAuditPhotos(newPhotos);

    // Create preview URLs
    const newUrls = newPhotos.map(file => URL.createObjectURL(file));
    // Revoke old URLs to prevent memory leaks
    photoPreviewUrls.forEach(url => URL.revokeObjectURL(url));
    setPhotoPreviewUrls(newUrls);
  };

  const handlePhotoRemove = (index: number) => {
    URL.revokeObjectURL(photoPreviewUrls[index]);
    setAuditPhotos(prev => prev.filter((_, i) => i !== index));
    setPhotoPreviewUrls(prev => prev.filter((_, i) => i !== index));
  };

  // Get status color helper
  const getStatusColor = (s: string) => {
    switch (s) {
      case 'found': return 'green';
      case 'missing': return 'red';
      case 'damaged': return 'orange';
      default: return 'gray';
    }
  };

  return (
    <Box style={{ minHeight: '100vh', paddingBottom: '80px' }}>
      <Container size="md" py="md">
        <Stack gap="md">
          {/* Breadcrumbs */}
          <Breadcrumbs size="sm">
            <Anchor onClick={() => navigate('/audits')}>Audits</Anchor>
            <Anchor onClick={() => navigate(`/audits/${auditId}`)}>{audit.name}</Anchor>
            <Text>Review</Text>
          </Breadcrumbs>

          {/* Mode Toggle & Progress */}
          <Paper p="sm" withBorder>
            <Stack gap="sm">
              <SegmentedControl
                value={reviewMode}
                onChange={(value) => {
                  setReviewMode(value as 'sequential' | 'scan');
                  setScannedAsset(null);
                  setScanNotFound(null);
                  setScanSearchQuery('');
                }}
                data={[
                  { label: <Group gap={4}><IconList size={14} /><span>Sequential</span></Group>, value: 'sequential' },
                  { label: <Group gap={4}><IconQrcode size={14} /><span>Scan</span></Group>, value: 'scan' },
                ]}
                fullWidth
              />
              <Group justify="space-between">
                <Text size="sm" fw={500}>Progress</Text>
                <Text size="sm" c="dimmed">{completed} / {assets.length}</Text>
              </Group>
              <Progress value={progress} size="sm" />
            </Stack>
          </Paper>

          {/* Scan Mode - Scanner UI (when no asset selected) */}
          {reviewMode === 'scan' && !scannedAsset && (
            <Paper p="md" withBorder>
              <Stack gap="md">
                {/* Manual search input */}
                <Group gap="xs">
                  <TextInput
                    placeholder="Enter tag ID or serial number..."
                    value={scanSearchQuery}
                    onChange={(e) => setScanSearchQuery(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleManualSearch()}
                    style={{ flex: 1 }}
                    leftSection={<IconSearch size={16} />}
                  />
                  <Button onClick={handleManualSearch} disabled={!scanSearchQuery.trim()}>
                    Find
                  </Button>
                </Group>

                {scanNotFound && (
                  <Alert color="orange" icon={<IconAlertCircle size={16} />}>
                    <Text size="sm">
                      Asset "{scanNotFound}" not found in this audit.
                    </Text>
                  </Alert>
                )}

                {/* QR Scanner */}
                <QRScanner
                  onScan={handleScanResult}
                />

                <Text size="xs" c="dimmed" ta="center">
                  Scan a QR code or barcode on the asset, or type the tag ID above
                </Text>
              </Stack>
            </Paper>
          )}

          {/* Asset Review Card - Same UI for both sequential and scan modes */}
          {(reviewMode === 'sequential' || (reviewMode === 'scan' && scannedAsset)) && (() => {
            // Use scannedAsset in scan mode, current in sequential mode
            const activeItem = reviewMode === 'scan' ? scannedAsset : current;
            if (!activeItem) return null;

            return (
          <Paper p="md" withBorder>
            <Stack gap="md">
              {/* Header: Asset ID, Description, Photo */}
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <div style={{ flex: 1, minWidth: 0 }}>
                  {reviewMode === 'sequential' ? (
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                      Asset {currentIndex + 1} of {assets.length}
                    </Text>
                  ) : (
                    <Badge
                      color={activeItem.status === 'pending' ? 'gray' : activeItem.status === 'found' ? 'green' : activeItem.status === 'missing' ? 'red' : 'orange'}
                      mb="xs"
                      size="sm"
                    >
                      {activeItem.status === 'pending' ? 'Not yet reviewed' : `Previously: ${activeItem.status}`}
                    </Badge>
                  )}
                  <Title order={2} style={{ wordBreak: 'break-word' }}>
                    {activeItem.asset?.tag_id || 'Unknown'}
                  </Title>
                  <Text size="sm" c="dimmed" lineClamp={2}>
                    {activeItem.asset?.description || 'No description'}
                  </Text>
                </div>
                {activeItem.asset?.photo_url && (
                  <Image
                    src={activeItem.asset.photo_url}
                    alt={activeItem.asset.tag_id || 'Asset'}
                    w={100}
                    h={100}
                    fit="cover"
                    radius="md"
                    style={{ flexShrink: 0 }}
                  />
                )}
              </Group>

              {/* Location - Prominent Display */}
              <Paper p="sm" bg="blue.0" radius="md">
                <Group gap="xs">
                  <ThemeIcon size="sm" variant="light" color="blue">
                    <IconMapPin size={14} />
                  </ThemeIcon>
                  <div>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Expected Location</Text>
                    <Text size="sm" fw={500}>
                      {activeItem.asset?.site?.name || activeItem.asset?.location?.name
                        ? [activeItem.asset?.site?.name, activeItem.asset?.location?.name].filter(Boolean).join(' → ')
                        : 'No location assigned'}
                    </Text>
                  </div>
                </Group>
              </Paper>

              {/* Key Details Grid */}
              <SimpleGrid cols={2} spacing="xs">
                <Box>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Category</Text>
                  <Badge size="sm" color="blue" variant="light" mt={2}>
                    {activeItem.asset?.category?.name || 'Uncategorized'}
                  </Badge>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Brand</Text>
                  <Text size="sm" fw={500}>{activeItem.asset?.brand || '—'}</Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Model</Text>
                  <Text size="sm" fw={500}>{activeItem.asset?.model || '—'}</Text>
                </Box>
                <Box>
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Serial Number</Text>
                  <Text size="sm" fw={500} style={{ wordBreak: 'break-all' }}>
                    {activeItem.asset?.serial_number || '—'}
                  </Text>
                </Box>
              </SimpleGrid>

              {/* More Details - Collapsible */}
              <Button
                variant="subtle"
                size="xs"
                onClick={() => setShowMoreDetails(!showMoreDetails)}
                rightSection={showMoreDetails ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
              >
                {showMoreDetails ? 'Hide Details' : 'More Details'}
              </Button>

              <Collapse in={showMoreDetails}>
                <Paper p="sm" bg="gray.0" radius="md">
                  <SimpleGrid cols={2} spacing="xs">
                    <Box>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Status</Text>
                      <Badge
                        size="sm"
                        mt={2}
                        color={
                          activeItem.asset?.status === 'Available' ? 'green' :
                          activeItem.asset?.status === 'Assigned' ? 'blue' :
                          activeItem.asset?.status === 'Maintenance' ? 'orange' :
                          activeItem.asset?.status === 'Retired' ? 'gray' : 'red'
                        }
                      >
                        {activeItem.asset?.status || 'Unknown'}
                      </Badge>
                    </Box>
                    <Box>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Department</Text>
                      <Text size="sm" fw={500}>{activeItem.asset?.department?.name || '—'}</Text>
                    </Box>
                    {activeItem.asset?.cost && (
                      <Box>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Cost</Text>
                        <Text size="sm" fw={500}>${activeItem.asset.cost.toLocaleString()}</Text>
                      </Box>
                    )}
                    {activeItem.asset?.purchase_date && (
                      <Box>
                        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Purchase Date</Text>
                        <Text size="sm" fw={500}>
                          {new Date(activeItem.asset.purchase_date).toLocaleDateString()}
                        </Text>
                      </Box>
                    )}
                  </SimpleGrid>
                </Paper>
              </Collapse>

              {/* Audit Status Selection */}
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb="xs">Audit Result</Text>
                <SegmentedControl
                  value={status}
                  onChange={(val) => setStatus(val as any)}
                  data={[
                    { label: 'Pending', value: 'pending' },
                    { label: 'Found', value: 'found' },
                    { label: 'Missing', value: 'missing' },
                    { label: 'Damaged', value: 'damaged' },
                  ]}
                  fullWidth
                  color={getStatusColor(status)}
                />
              </Box>

              {/* Notes */}
              <Box>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb="xs">Notes</Text>
                <Textarea
                  placeholder="Add audit notes..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  minRows={2}
                  maxRows={4}
                  autosize
                />
              </Box>

              {/* Photo Evidence */}
              <Box>
                <Group justify="space-between" mb="xs">
                  <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                    Photo Evidence ({auditPhotos.length}/4)
                  </Text>
                  <FileButton
                    onChange={(files) => files && handlePhotoAdd(Array.isArray(files) ? files : [files])}
                    accept="image/*"
                    multiple
                    disabled={auditPhotos.length >= 4}
                  >
                    {(props) => (
                      <Button
                        {...props}
                        size="xs"
                        variant="light"
                        leftSection={<IconCamera size={14} />}
                        disabled={auditPhotos.length >= 4}
                      >
                        Add Photo
                      </Button>
                    )}
                  </FileButton>
                </Group>

                {photoPreviewUrls.length > 0 && (
                  <SimpleGrid cols={4} spacing="xs">
                    {photoPreviewUrls.map((url, index) => (
                      <Box key={index} pos="relative">
                        <Image
                          src={url}
                          alt={`Evidence ${index + 1}`}
                          h={60}
                          w="100%"
                          fit="cover"
                          radius="sm"
                        />
                        <ActionIcon
                          size="xs"
                          color="red"
                          variant="filled"
                          pos="absolute"
                          top={2}
                          right={2}
                          onClick={() => handlePhotoRemove(index)}
                        >
                          <IconX size={10} />
                        </ActionIcon>
                      </Box>
                    ))}
                  </SimpleGrid>
                )}

                {auditPhotos.length === 0 && (
                  <Paper p="sm" bg="gray.0" radius="md" ta="center">
                    <IconPhoto size={20} color="gray" />
                    <Text size="xs" c="dimmed" mt={4}>
                      No photos added
                    </Text>
                  </Paper>
                )}
              </Box>
            </Stack>
          </Paper>
            );
          })()}
        </Stack>
      </Container>

      {/* Fixed Bottom Navigation - Sequential mode */}
      {reviewMode === 'sequential' && (
      <Box
        pos="fixed"
        bottom={0}
        left={0}
        right={0}
        bg="white"
        py="sm"
        px="md"
        style={{
          borderTop: '1px solid var(--mantine-color-gray-3)',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
          zIndex: 100,
        }}
      >
        <Container size="md">
          <Group justify="space-between" wrap="nowrap">
            <Button
              variant="default"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
              disabled={currentIndex === 0}
              style={{ flex: 1, maxWidth: '120px' }}
            >
              Previous
            </Button>

            <Button
              onClick={saveAndNext}
              style={{ flex: 2, maxWidth: '200px' }}
              color={currentIndex === assets.length - 1 ? 'green' : 'blue'}
            >
              {currentIndex === assets.length - 1 ? 'Finish Audit' : 'Save & Next'}
            </Button>

            <Button
              variant="default"
              rightSection={<IconArrowRight size={16} />}
              onClick={() => setCurrentIndex(prev => Math.min(assets.length - 1, prev + 1))}
              disabled={currentIndex === assets.length - 1}
              style={{ flex: 1, maxWidth: '120px' }}
            >
              Next
            </Button>
          </Group>
        </Container>
      </Box>
      )}

      {/* Fixed Bottom Navigation - Scan mode (when asset is selected) */}
      {reviewMode === 'scan' && scannedAsset && (
      <Box
        pos="fixed"
        bottom={0}
        left={0}
        right={0}
        bg="white"
        py="sm"
        px="md"
        style={{
          borderTop: '1px solid var(--mantine-color-gray-3)',
          boxShadow: '0 -2px 10px rgba(0,0,0,0.1)',
          zIndex: 100,
        }}
      >
        <Container size="md">
          <Group justify="space-between" wrap="nowrap">
            <Button
              variant="default"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => {
                setScannedAsset(null);
                setNotes('');
                setAuditPhotos([]);
                setPhotoPreviewUrls([]);
              }}
              style={{ flex: 1, maxWidth: '140px' }}
            >
              Back to Scan
            </Button>

            <Button
              onClick={() => quickSave(status === 'pending' ? 'found' : status)}
              style={{ flex: 2, maxWidth: '200px' }}
              color="green"
            >
              Save & Scan Another
            </Button>
          </Group>
        </Container>
      </Box>
      )}
    </Box>
  );
}
